/**
 * Demo / showcase reference surveys for the native builder (VER-108/109).
 *
 * These are NOT clinical instruments —
 * they exist to exercise every question type and the branching engine so the builder
 * and the Survey Results screens have varied content to demonstrate. All are
 * `isSystemSeed` (read-only; the builder clones them to edit).
 *
 * Frontend-only, mock data. No backend / API coupling.
 */
import type { Section, SurveyDefinition, SurveyQuestion } from "./survey.types";

/** Terse question builder — sensible defaults, override per question. */
const q = (
    partial: Partial<SurveyQuestion> & Pick<SurveyQuestion, "id" | "order" | "text" | "type">,
): SurveyQuestion => ({
    reverseScored: false,
    scored: true,
    required: true,
    ...partial,
});

const section = (
    id: string,
    order: number,
    title: string,
    questions: SurveyQuestion[],
    description?: string,
): Section => ({
    id,
    order,
    title,
    description,
    questions,
});

// ─────────────────────────────────────────────────────────────────────────────
// 1. Session Feedback — mixes single / NPS / multi / free-text
// ─────────────────────────────────────────────────────────────────────────────
export const SESSION_FEEDBACK_SURVEY: SurveyDefinition = {
    id: "seed-session-feedback",
    code: "SESSFB",
    title: "Session Feedback",
    instructions: "A short feedback form completed at the end of a session. Tell us how it went.",
    higherIsBetter: true,
    status: "published",
    version: 1,
    defaultScale: { options: [] },
    sections: [
        section("section-1", 1, "Your session", [
            q({
                id: "q1",
                order: 1,
                text: "How would you rate today's session overall?",
                type: "single",
                options: [
                    { label: "Poor", value: 1 },
                    { label: "Fair", value: 2 },
                    { label: "Good", value: 3 },
                    { label: "Very good", value: 4 },
                    { label: "Excellent", value: 5 },
                ],
            }),
            q({
                id: "q2",
                order: 2,
                text: "How likely are you to recommend this programme to a friend?",
                type: "nps",
            }),
            q({
                id: "q3",
                order: 3,
                text: "What did you find most helpful? (select all that apply)",
                type: "multi",
                scored: false,
                required: false,
                options: [
                    { label: "The stories", value: 1 },
                    { label: "Group discussion", value: 2 },
                    { label: "The facilitator", value: 3 },
                    { label: "Meeting others", value: 4 },
                    { label: "The activities", value: 5 },
                ],
            }),
            q({
                id: "q4",
                order: 4,
                text: "Anything else you'd like to share?",
                type: "freeText",
                scored: false,
                required: false,
                freeText: { multiline: true, maxLength: 500, placeholder: "Your comments (optional)" },
            }),
        ]),
    ],
    scoring: { method: "sum", rawRange: [1, 15] }, // single 1–5 + nps 0–10
    branchingRules: [],
    isSystemSeed: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. Wellbeing Check-in — BRANCHING demo (a gate reveals a follow-up section)
// ─────────────────────────────────────────────────────────────────────────────
export const WELLBEING_CHECKIN_SURVEY: SurveyDefinition = {
    id: "seed-wellbeing-checkin",
    code: "WBCHK",
    title: "Wellbeing Check-in (branching)",
    instructions: "A quick weekly check-in. If things are tough, a short follow-up appears so we can offer support.",
    higherIsBetter: true,
    status: "published",
    version: 1,
    defaultScale: { options: [] },
    sections: [
        section("section-1", 1, "This week", [
            q({
                id: "q1",
                order: 1,
                text: "Overall, how are you feeling this week?",
                type: "single",
                options: [
                    { label: "Really tough", value: 1 },
                    { label: "Struggling", value: 2 },
                    { label: "Okay", value: 3 },
                    { label: "Great", value: 4 },
                ],
            }),
        ]),
        section(
            "section-2",
            2,
            "Tell us more",
            [
                q({
                    id: "q2",
                    order: 1,
                    text: "What's been the hardest part?",
                    type: "freeText",
                    scored: false,
                    required: false,
                    freeText: { multiline: true, maxLength: 500 },
                }),
                q({
                    id: "q3",
                    order: 2,
                    text: "Would you like someone to check in with you?",
                    type: "single",
                    scored: false,
                    required: false,
                    options: [
                        { label: "No, I'm okay", value: 0 },
                        { label: "Yes, please", value: 1 },
                    ],
                }),
            ],
            "Shown only when the check-in indicates a tough week.",
        ),
    ],
    scoring: { method: "sum", rawRange: [1, 4] },
    // Section 2 is HIDDEN by default and revealed when q1 ≤ 2 (struggling / really tough).
    branchingRules: [
        {
            id: "rule-1",
            sourceQuestionId: "q1",
            operator: "lte",
            value: 2,
            action: "show",
            targetType: "section",
            targetId: "section-2",
        },
    ],
    isSystemSeed: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. Programme Experience — VAS sliders + a Likert agreement item
// ─────────────────────────────────────────────────────────────────────────────
export const PROGRAMME_EXPERIENCE_SURVEY: SurveyDefinition = {
    id: "seed-programme-experience",
    code: "PEXP",
    title: "Programme Experience",
    instructions: "Reflect on your experience of the programme so far.",
    higherIsBetter: true,
    status: "published",
    version: 1,
    defaultScale: { options: [] },
    sections: [
        section("section-1", 1, "Your experience", [
            q({
                id: "q1",
                order: 1,
                text: "How connected did you feel to the group?",
                type: "vas",
                vas: { min: 0, max: 10, minLabel: "Not at all", maxLabel: "Very connected" },
            }),
            q({
                id: "q2",
                order: 2,
                text: "How confident do you feel about your wellbeing right now?",
                type: "vas",
                vas: { min: 0, max: 10, minLabel: "Not confident", maxLabel: "Very confident" },
            }),
            q({
                id: "q3",
                order: 3,
                text: "The programme met my expectations.",
                type: "likert",
                options: [
                    { label: "Strongly disagree", value: 1 },
                    { label: "Disagree", value: 2 },
                    { label: "Neutral", value: 3 },
                    { label: "Agree", value: 4 },
                    { label: "Strongly agree", value: 5 },
                ],
            }),
        ]),
    ],
    scoring: { method: "sum", rawRange: [1, 25] }, // vas 0–10 ×2 + likert 1–5
    branchingRules: [],
    isSystemSeed: true,
};

/** The showcase demo surveys, in display order. */
export const DEMO_SURVEYS: SurveyDefinition[] = [
    SESSION_FEEDBACK_SURVEY,
    WELLBEING_CHECKIN_SURVEY,
    PROGRAMME_EXPERIENCE_SURVEY,
];
